import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Admin.scss';

import { fetchAuthSession } from 'aws-amplify/auth';

// Font Awesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faSearch, faShieldAlt, faNetworkWired } from '@fortawesome/free-solid-svg-icons';

// AWS SDK v2 for Cognito
import { CognitoIdentityServiceProvider } from 'aws-sdk';

const AdminPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [adminUsernames, setAdminUsernames] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);

  // For searching and sorting
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState('email');
  const [sortDirection, setSortDirection] = useState('asc');

  // For creating new user
  const [newEmail, setNewEmail] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');

  // Whether the current user is admin (from sessionStorage or similar approach)
  const [isAdmin, setIsAdmin] = useState(false);

  let cognitoClientRef = React.useRef(null);

  // On mount: check cached user, then fetch AWS credentials to set up Cognito client
  useEffect(() => {
    const initialize = async () => {
      try {
        // 1) Check if we already have user info in session storage
        const cachedUser = sessionStorage.getItem('currentUser');
        if (cachedUser) {
          const user = JSON.parse(cachedUser);
          setIsAdmin(!!user.isAdmin);
        }

        // 2) Fetch the session for AWS credentials
        const session = await fetchAuthSession();
        if (!session?.credentials) {
          console.error('No credentials returned by fetchAuthSession.');
          return;
        }

        const region = 'us-east-1'; // e.g., 'us-east-1'
        cognitoClientRef.current = new CognitoIdentityServiceProvider({
          region,
          credentials: session.credentials
        });

        // Now fetch the admin group and the users
        await fetchAdminUsers();
        await listAllUsers();
      } catch (error) {
        console.error('Error initializing Admin page:', error);
      }
    };

    initialize();
  }, []);

  /**
   * Loop through all pages of the "administrator" group to collect admin usernames.
   */
  const fetchAdminUsers = async () => {
    if (!cognitoClientRef.current) return;
    try {
      let nextToken = null;
      let adminSet = new Set();

      do {
        const resp = await cognitoClientRef.current
          .listUsersInGroup({
            GroupName: 'administrator',
            UserPoolId: 'us-east-1_kqIkEeN5X', // YOUR_USER_POOL_ID
            Limit: 60, // can be up to 60
            ...(nextToken ? { NextToken: nextToken } : {})
          })
          .promise();

        (resp.Users || []).forEach((u) => adminSet.add(u.Username));
        nextToken = resp.NextToken || null;
      } while (nextToken);

      setAdminUsernames(adminSet);
    } catch (error) {
      console.error('Error fetching admin group users:', error);
    }
  };

  /**
   * Fetch *all* users from the user pool by looping through pagination until we have them all.
   * If "searchInput" is non-empty, we apply a filter for "email ^= 'prefix'".
   */
  const listAllUsers = async () => {
    if (!cognitoClientRef.current) return;
    setIsLoading(true);

    const filterStr = searchInput.trim();
    let allUsers = [];
    let paginationToken = null;

    try {
      do {
        const params = {
          UserPoolId: 'us-east-1_kqIkEeN5X', // YOUR_USER_POOL_ID
          Limit: 60 // maximum per call
        };
        if (paginationToken) {
          params.PaginationToken = paginationToken;
        }
        if (filterStr) {
          // If user typed something in search, do a "starts with" filter
          params.Filter = `email ^= "${filterStr}"`;
        }

        const data = await cognitoClientRef.current.listUsers(params).promise();
        allUsers = [...allUsers, ...(data.Users || [])];

        paginationToken = data.PaginationToken || null;
      } while (paginationToken);

      console.log('All fetched users:', allUsers);
      setUsers(allUsers);
    } catch (error) {
      console.error('Error listing users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Called on "Search" button click
  const handleSearch = async () => {
    setUsers([]);
    await listAllUsers();
  };

  // Create a new user
  const handleCreateUser = async () => {
    if (!cognitoClientRef.current) return;
    if (!newEmail || !newDisplayName) {
      alert('Please provide an email and a display name.');
      return;
    }

    try {
      await cognitoClientRef.current
        .adminCreateUser({
          UserPoolId: 'us-east-1_kqIkEeN5X',
          Username: newEmail,
          UserAttributes: [
            { Name: 'email', Value: newEmail },
            { Name: 'email_verified', Value: 'true' },
            { Name: 'custom:display_name', Value: newDisplayName }
          ],
          TemporaryPassword: 'TempPassword123!'
        })
        .promise();

      alert(`User "${newEmail}" created successfully!`);

      setNewEmail('');
      setNewDisplayName('');

      // Refresh the user list
      setUsers([]);
      await fetchAdminUsers();
      await listAllUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Failed to create user.');
    }
  };

  // Delete a user
  const handleDeleteUser = async (username) => {
    if (!cognitoClientRef.current) return;
    const confirmed = window.confirm(
      `Are you sure you want to delete user "${username}"?`
    );
    if (!confirmed) return;

    try {
      await cognitoClientRef.current
        .adminDeleteUser({
          UserPoolId: 'us-east-1_kqIkEeN5X',
          Username: username
        })
        .promise();

      alert(`User "${username}" deleted successfully!`);
      setUsers((prev) => prev.filter((u) => u.Username !== username));
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user.');
    }
  };

  // Sorting logic
  const handleSort = (columnKey) => {
    if (sortBy === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(columnKey);
      setSortDirection('asc');
    }
  };

  const sortedUsers = [...users].sort((a, b) => {
    const valA = getColumnValue(a, sortBy);
    const valB = getColumnValue(b, sortBy);
    if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
    if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  function getColumnValue(user, columnKey) {
    if (columnKey === 'email') {
      const emailAttr = user.Attributes.find((attr) => attr.Name === 'email');
      return emailAttr ? emailAttr.Value.toLowerCase() : '';
    } else if (columnKey === 'status') {
      return user.UserStatus || '';
    } else if (columnKey === 'displayName') {
      const dispAttr = user.Attributes.find(
        (attr) => attr.Name === 'custom:display_name'
      );
      return dispAttr ? dispAttr.Value.toLowerCase() : '';
    }
    return '';
  }

  // If user is not admin, show a permission notice
  return (
    <div className="admin-page">
      <h2>Admin User Management</h2>
      {!isAdmin ? (
        <p>You do not have permission to access this page.</p>
      ) : (
        <div>
          {/* ADMIN TOOLS */}
          <div className="admin-tools">
            <button onClick={() => navigate('/admin/unallocated')}>
              <FontAwesomeIcon icon={faNetworkWired} /> Unallocated EVO-NetLinks
            </button>
          </div>

          {/* CREATE NEW USER FORM */}
          <div className="create-user-form">
            <h3>Create a New User</h3>
            <input
              type="text"
              placeholder="Email address"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
            />
            <input
              type="text"
              placeholder="Display Name"
              value={newDisplayName}
              onChange={(e) => setNewDisplayName(e.target.value)}
            />
            <button onClick={handleCreateUser}>Create User</button>
          </div>

          {/* SEARCH BAR */}
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search by email (prefix match)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button onClick={handleSearch}>
              <FontAwesomeIcon icon={faSearch} /> Search
            </button>
          </div>

          {/* TABLE */}
          <div className="table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('email')}>
                    Email
                    {sortBy === 'email' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                  </th>
                  <th onClick={() => handleSort('status')}>
                    Status
                    {sortBy === 'status' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                  </th>
                  <th onClick={() => handleSort('displayName')}>
                    Display Name
                    {sortBy === 'displayName' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map((user) => {
                  const emailAttr = user.Attributes.find(
                    (attr) => attr.Name === 'email'
                  );
                  const displayNameAttr = user.Attributes.find(
                    (attr) => attr.Name === 'custom:display_name'
                  );
                  const isUserAdmin = adminUsernames.has(user.Username);

                  return (
                    <tr key={user.Username}>
                      <td>
                        {emailAttr?.Value}{' '}
                        {isUserAdmin && (
                          <FontAwesomeIcon icon={faShieldAlt} title="Administrator" />
                        )}
                      </td>
                      <td>{user.UserStatus}</td>
                      <td>{displayNameAttr?.Value || ''}</td>
                      <td>
                        {/* Delete button (only if user is not admin) */}
                        {!isUserAdmin && (
                          <button
                            className="delete-btn"
                            onClick={() => handleDeleteUser(user.Username)}
                          >
                            <FontAwesomeIcon icon={faTrash} /> Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {users.length === 0 && !isLoading && (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', fontStyle: 'italic' }}>
                      No users found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {isLoading && <p>Loading users...</p>}
        </div>
      )}
    </div>
  );
};

export default AdminPage;
